// Per-LEVEL review of a Takt train apply — the data behind the "review every
// level before you commit" workflow. A train maps onto several WBS leaves
// (levels); applying it CREATES the elements a level lacks, RESIZES the ones it
// has to the template duration, and mirrors the internal ties — leaving
// completed / in-progress work locked. The user must see all of that, per level,
// as a true before/after, and be able to switch any single row off.
//
// This module is the pure core: buildTrainApplyReview() turns the train + board
// context into level groups of change rows (each carrying its concrete plan
// fragment), and reviewToPlan() collapses the review — minus any excluded
// (level, element) rows — back into the exact TrainApplyPlan the host already
// stages. planTrainApply (trainApply.ts) is re-expressed on top of these two, so
// there is ONE reconcile code path.
import type {
  TrainState,
  TrainInstance,
  TrainCar,
  TrainDepType,
} from "../../../../models";
import type {
  TrainApplyContext,
  InstanceLeaf,
  TrainCreate,
  TrainApplyPlan,
  ReviewRow,
  ReviewTie,
  ReviewContextTie,
  ReviewLevel,
  TrainApplyReview,
} from "../../../../models";

export type {
  ReviewRowKind,
  ReviewRow,
  ReviewTie,
  ReviewContextTie,
  ReviewLevel,
  TrainApplyReview,
} from "../../../../models";

/** Key an excluded (level, element) pair in the modal's exclusion set. */
export function exclusionKey(instanceIndex: number, carId: string): string {
  return `${instanceIndex}:${carId}`;
}

/** Build the per-level review. Mirrors planTrainApply's per-instance reconcile
 *  exactly — anchor at the leftmost placed non-locked bar, create the missing
 *  elements, sync durations, mirror ties — but records every step as a row/tie
 *  instead of folding straight into a flat plan. */
export function buildTrainApplyReview(
  train: TrainState,
  ctx: TrainApplyContext,
): TrainApplyReview {
  // The AFTER mini-gantt position AND what a CREATE actually WRITES both follow
  // the train's HAND-PLACED layout (car.startIndex), normalized to the train's
  // leftmost car — so an untied run (no internal FS chain) keeps its 0/4/8 spacing
  // instead of stacking every created bar on the anchor day (simulateTrain would
  // put them all at day 0), and the bar the user reviews lands where it commits.
  const minIdx = train.cars.length
    ? Math.min(...train.cars.map((c) => c.startIndex))
    : 0;
  const placeByCar = new Map(
    train.cars.map((c) => [c.carId, Math.max(0, c.startIndex - minIdx)]),
  );
  const durByCar = new Map(train.cars.map((c) => [c.carId, c.durationDays]));
  // A car is "duration-edited" iff its current duration diverges from the
  // baseline captured at build time — NOT from each instance's current bar.
  const durEditedByCar = new Map(
    train.cars.map((c) => [c.carId, c.durationDays !== c.baselineDurationDays]),
  );
  const carByCarId: Record<string, TrainCar> = {};
  for (const c of train.cars) carByCarId[c.carId] = c;

  const levels: ReviewLevel[] = [];

  train.instances.forEach((inst, instanceIndex) => {
    const augmented: TrainInstance = { ...inst };

    // Anchor + leaf, exactly as planTrainApply does.
    let anchor = Infinity;
    let leaf: InstanceLeaf | null = null;
    for (const ref of Object.values(inst)) {
      if (!leaf) leaf = ctx.leafOf(ref);
      const s = ctx.startOf(ref);
      if (s != null && s >= 0) anchor = Math.min(anchor, s);
    }
    if (!Number.isFinite(anchor)) anchor = 0;

    const rows: ReviewRow[] = [];
    const createdCarIds = new Set<string>();

    // One row per train car, in canonical order.
    for (const car of train.cars) {
      const afterStart = placeByCar.get(car.carId) ?? 0;
      const afterDuration = durByCar.get(car.carId) ?? 1;
      const existing = augmented[car.carId];

      if (!existing) {
        // Element missing on this level → CREATE (needs a leaf to land on).
        if (!leaf) continue;
        const draftId = ctx.newId();
        const leafLabel = leaf.wbsPath[leaf.wbsPath.length - 1] ?? leaf.wbsKey;
        const create: TrainCreate = {
          carId: car.carId,
          draftId,
          wbsKey: leaf.wbsKey,
          wbsPath: leaf.wbsPath,
          wbsNodeId: leaf.wbsNodeId,
          startIndex: anchor + afterStart,
          durationDays: afterDuration,
          code: car.code,
          name: `${ctx.nameOf(car.code)} | ${leafLabel}`,
        };
        augmented[car.carId] = draftId;
        createdCarIds.add(car.carId);
        rows.push({
          carId: car.carId,
          code: car.code,
          name: create.name,
          kind: "create",
          ref: draftId,
          isCommitted: false,
          locked: false,
          beforeDuration: null,
          afterDuration,
          beforeStart: null,
          afterStart,
          create,
        });
        continue;
      }

      const locked = ctx.isLocked(existing);
      const beforeDuration = ctx.durationOf(existing);
      const beforeStart = ctx.startOf(existing);
      const isCommitted = ctx.isCommitted(existing);
      if (locked) {
        rows.push({
          carId: car.carId,
          code: car.code,
          name: ctx.nameOf(car.code),
          kind: "locked",
          ref: existing,
          isCommitted,
          locked: true,
          beforeDuration,
          afterDuration,
          beforeStart,
          afterStart,
        });
        continue;
      }
      // Resize ONLY when the user edited this car's duration on the template AND
      // this instance's bar isn't already at that length. An instance whose bar
      // merely differs from the template is left alone (Apply is a pure delta of
      // EDITS, not a re-sync), and one that already equals the new length needs
      // no physical write — keeping Apply strictly minimally-invasive.
      const edited =
        (durEditedByCar.get(car.carId) ?? false) &&
        beforeDuration !== afterDuration;
      rows.push({
        carId: car.carId,
        code: car.code,
        name: ctx.nameOf(car.code),
        kind: edited ? "resize" : "unchanged",
        ref: existing,
        isCommitted,
        locked: false,
        beforeDuration,
        afterDuration,
        beforeStart,
        afterStart,
        ...(edited
          ? {
              dur: isCommitted
                ? { committed: true, id: Number(existing), days: afterDuration }
                : { committed: false, draftId: existing, days: afterDuration },
            }
          : {}),
      });
    }

    // Ties — a pure DELTA of the template's tie EDITS (deps vs baselineDeps),
    // projected onto this instance. A tie the user never touched emits nothing,
    // so a no-change Apply never deletes a real, untouched board tie.
    const ties: ReviewTie[] = [];
    const refToCar = new Map<string, string>();
    for (const [carId, ref] of Object.entries(augmented)) refToCar.set(ref, carId);

    const depKey = (d: { predCarId: string; succCarId: string }) =>
      `${d.predCarId}>${d.succCarId}`;
    const baselineByPair = new Map(train.baselineDeps.map((d) => [depKey(d), d]));
    const currentByPair = new Map(train.deps.map((d) => [depKey(d), d]));

    const pushAdd = (
      d: (typeof train.deps)[number],
      restate = false,
      forCreate = false,
    ) => {
      const p = augmented[d.predCarId];
      const s = augmented[d.succCarId];
      if (!p || !s) return;
      // A STRUCTURAL create-tie ties a brand-new (never-locked) draft after its
      // train predecessor — valid and REQUIRED for the forward pass to sequence
      // it, even when that predecessor is completed/in-progress. Only the
      // re-tie-two-existing-bars paths honor the locked guard.
      if (!forCreate && (ctx.isLocked(p) || ctx.isLocked(s))) return;
      // On a retype, the board still carries the OLD edge here; a matching
      // pushRemove was already queued and commits BEFORE this add, so skip the
      // dedupe guard and restate the tie with its new type/lag.
      if (!restate && ctx.hasEdge(p, s)) return;
      ties.push({
        kind: "add",
        predCarId: d.predCarId,
        succCarId: d.succCarId,
        pred: p,
        succ: s,
        type: d.type,
        lag: d.lagDays,
        predCode: carByCarId[d.predCarId]?.code ?? "",
        succCode: carByCarId[d.succCarId]?.code ?? "",
      });
    };
    const pushRemove = (d: { predCarId: string; succCarId: string }) => {
      const p = augmented[d.predCarId];
      const s = augmented[d.succCarId];
      if (!p || !s) return;
      if (ctx.isLocked(p) || ctx.isLocked(s)) return;
      const edge = ctx.edgeType(p, s);
      if (!edge) return; // board doesn't have it — nothing to remove
      ties.push({
        kind: "remove",
        predCarId: d.predCarId,
        succCarId: d.succCarId,
        pred: p,
        succ: s,
        type: edge.type,
        lag: edge.lag,
        predCode: carByCarId[d.predCarId]?.code ?? "",
        succCode: carByCarId[d.succCarId]?.code ?? "",
      });
    };

    // Added: in deps, not in baseline. If the board already carries an edge for
    // this pair, the authored tie REPLACES it: same type/lag is a no-op; a
    // different type/lag is a remove-old + restate (so the user's edit reaches
    // the board instead of being swallowed by a stale, wrong edge).
    currentByPair.forEach((d, key) => {
      if (baselineByPair.has(key)) return;
      const p = augmented[d.predCarId];
      const s = augmented[d.succCarId];
      if (!p || !s) return;
      const edge = ctx.edgeType(p, s);
      if (!edge) {
        pushAdd(d);
      } else if (edge.type === d.type && edge.lag === d.lagDays) {
        // identical tie already on the board → nothing to do
      } else {
        pushRemove(d);
        pushAdd(d, true);
      }
    });
    // Removed: in baseline, not in deps.
    baselineByPair.forEach((d, key) => {
      if (!currentByPair.has(key)) pushRemove(d);
    });
    // Retyped: in both with a changed type/lag. A retype means "change my
    // EXISTING tie" — so only act on a level that actually carries the old board
    // edge: remove it, then restate with the new type/lag. On a level WITHOUT
    // that edge there is nothing to retype, so emit nothing (never inject a tie
    // the user never had on that level).
    currentByPair.forEach((cur, key) => {
      const base = baselineByPair.get(key);
      if (!base) return;
      if (base.type === cur.type && base.lagDays === cur.lagDays) return;
      const p = augmented[cur.predCarId];
      const s = augmented[cur.succCarId];
      if (!p || !s) return;
      const edge = ctx.edgeType(p, s);
      if (!edge) return; // no board edge here — nothing to retype
      if (edge.type === cur.type && edge.lag === cur.lagDays) return; // already at target → no-op
      pushRemove(cur);
      pushAdd(cur, true);
    });
    // Newly CREATED element: it carries none of the train's internal ties yet, so
    // even a STRUCTURAL tie (present in both deps and baseline → not a user-edit
    // delta, emitted by none of the blocks above) must be staged to wire the new
    // activity into the train chain. Without this the created bar lands orphaned
    // and the forward pass can't sequence it after its train predecessor.
    const queuedAddPairs = new Set(
      ties.filter((t) => t.kind === "add").map((t) => depKey(t)),
    );
    currentByPair.forEach((d, key) => {
      if (!createdCarIds.has(d.predCarId) && !createdCarIds.has(d.succCarId)) return;
      if (queuedAddPairs.has(key)) return; // already staged by a delta block
      pushAdd(d, false, true);
    });

    // Read-only context: board ties linking a mapped train ref to an activity
    // OUTSIDE the train. Surfaced for visibility, never staged — guarantees the
    // train preserves its in-level preds/succs.
    const contextTies: ReviewContextTie[] = [];
    const instanceRefs = new Set(Object.values(augmented));
    const seenContext = new Set<string>();
    Object.values(augmented).forEach((ref) => {
      const carId = refToCar.get(ref) ?? "";
      for (const e of ctx.tiesTouching(ref)) {
        const other = e.pred === ref ? e.succ : e.pred;
        if (instanceRefs.has(other)) continue; // in-train — handled by ties above
        const key = `${e.pred}>${e.succ}`;
        if (seenContext.has(key)) continue;
        seenContext.add(key);
        contextTies.push({
          pred: e.pred,
          succ: e.succ,
          type: e.type,
          lag: e.lag,
          carId,
          side: e.succ === ref ? "pred" : "succ",
        });
      }
    });

    // Record the board edge this level carried for every train dep-pair it maps
    // both ends of — so rebase can distinguish "already at target / landed" from
    // "dropped because the board lacked the edge this op needed".
    const trainEdges: Record<string, { type: TrainDepType; lag: number } | null> =
      {};
    const allPairs = new Map<string, { predCarId: string; succCarId: string }>();
    for (const d of train.deps) allPairs.set(depKey(d), d);
    for (const d of train.baselineDeps) allPairs.set(depKey(d), d);
    allPairs.forEach((d, key) => {
      const p = augmented[d.predCarId];
      const s = augmented[d.succCarId];
      if (!p || !s) return; // level doesn't map both ends — it never carried this pair
      trainEdges[key] = ctx.edgeType(p, s);
    });

    const leafLabel = leaf
      ? leaf.wbsPath[leaf.wbsPath.length - 1] ?? leaf.wbsKey
      : "(unplaced)";
    levels.push({
      instanceIndex,
      leaf,
      leafKey: leaf ? leaf.wbsKey : `inst-${instanceIndex}`,
      leafLabel,
      originalInstance: inst,
      rows,
      ties,
      contextTies,
      trainEdges,
    });
  });

  return { levels, carOrder: train.cars.map((c) => c.carId), carByCarId };
}

/** Collapse a review into a stageable plan, dropping any (level, element) in
 *  `excluded`. Excluding a row also drops the ties touching it on that level. */
export function reviewToPlan(
  review: TrainApplyReview,
  excluded: ReadonlySet<string> = new Set(),
): TrainApplyPlan {
  const plan: TrainApplyPlan = {
    creates: [],
    durCommitted: [],
    durDraft: [],
    tieAdds: [],
    tieRemovals: [],
    nextInstances: [],
  };

  for (const level of review.levels) {
    const isEx = (carId: string) =>
      excluded.has(exclusionKey(level.instanceIndex, carId));
    const augmented: TrainInstance = { ...level.originalInstance };

    for (const row of level.rows) {
      if (isEx(row.carId)) continue;
      if (row.kind === "create" && row.create) {
        plan.creates.push(row.create);
        augmented[row.carId] = row.create.draftId;
      } else if (row.kind === "resize" && row.dur) {
        if (row.dur.committed && row.dur.id != null) {
          plan.durCommitted.push({ id: row.dur.id, days: row.dur.days });
        } else if (row.dur.draftId != null) {
          plan.durDraft.push({ draftId: row.dur.draftId, days: row.dur.days });
        }
      }
    }

    for (const tie of level.ties) {
      if (tie.predCarId && isEx(tie.predCarId)) continue;
      if (tie.succCarId && isEx(tie.succCarId)) continue;
      if (tie.kind === "add") {
        const p = tie.predCarId ? augmented[tie.predCarId] : tie.pred;
        const s = tie.succCarId ? augmented[tie.succCarId] : tie.succ;
        if (!p || !s) continue; // an endpoint's create was excluded → can't tie
        plan.tieAdds.push({ pred: p, succ: s, type: tie.type, lag: tie.lag });
      } else {
        plan.tieRemovals.push({ pred: tie.pred, succ: tie.succ });
      }
    }

    plan.nextInstances.push(augmented);
  }

  return plan;
}

/** Rebase a train's baselines to what an Apply ACTUALLY wrote, per car / per dep
 *  — NOT a blanket "rebase everything when nothing was excluded".
 *
 *  An edit only folds into the baseline (so a re-Apply is a no-op) once it has
 *  landed on EVERY mapped instance. If any level dropped it — its matching bar is
 *  locked (the locked guard at pushAdd/pushRemove / the `locked` row) or the user
 *  excluded that (level, element) — the edit must stay a LIVE delta against the
 *  baseline, or it is swallowed forever and can never reach that level once it
 *  unlocks. A fully-applied edit still rebases, keeping re-Apply a strict no-op.
 *
 *  Returns the new `cars` (baselineDurationDays rebased per-car) and `baselineDeps`
 *  (current deps minus the not-fully-applied adds, plus the not-fully-applied
 *  removes kept). */
export function rebaseTrainBaselines(
  train: TrainState,
  review: TrainApplyReview,
  excluded: ReadonlySet<string> = new Set(),
): { cars: TrainState["cars"]; baselineDeps: TrainState["deps"] } {
  const isEx = (instanceIndex: number, carId: string) =>
    excluded.has(exclusionKey(instanceIndex, carId));

  // A car whose edit was dropped on at least one level (locked bar or excluded)
  // is NOT fully applied — leave its duration baseline alone so its edit stays live.
  const carDropped = new Set<string>();
  for (const level of review.levels) {
    for (const row of level.rows) {
      if (row.kind === "locked" || isEx(level.instanceIndex, row.carId)) {
        carDropped.add(row.carId);
      }
    }
  }

  const depKey = (d: { predCarId: string; succCarId: string }) =>
    `${d.predCarId}>${d.succCarId}`;

  // A dep pair's edit is "dropped" on a level iff that level maps BOTH endpoints
  // but the authored op couldn't land there. Two drop causes:
  //   1. an endpoint is locked or excluded (the locked guard / exclusion), OR
  //   2. (RETYPE only) the level maps both ends but its BOARD has NO edge for the
  //      pair — a retype transforms an EXISTING tie, so with nothing to retype the
  //      authored type never reaches that level. (An add lands whether or not the
  //      board already has an edge; a remove is satisfied by an already-absent edge
  //      — so neither is dropped by an absent board edge.)
  const depDropped = (
    predCarId: string,
    succCarId: string,
    isRetype = false,
  ): boolean => {
    const key = depKey({ predCarId, succCarId });
    for (const level of review.levels) {
      const inst = level.originalInstance;
      // Only levels that map BOTH endpoints (existing or about-to-be-created) can
      // carry this tie; a level missing an endpoint never had it, so it can't drop it.
      const mapsBoth =
        (inst[predCarId] !== undefined ||
          level.rows.some((r) => r.carId === predCarId && r.kind === "create")) &&
        (inst[succCarId] !== undefined ||
          level.rows.some((r) => r.carId === succCarId && r.kind === "create"));
      if (!mapsBoth) continue;
      const endpointBlocked = (carId: string) =>
        isEx(level.instanceIndex, carId) ||
        level.rows.some((r) => r.carId === carId && r.kind === "locked");
      if (endpointBlocked(predCarId) || endpointBlocked(succCarId)) return true;
      // A CREATED endpoint carries no board edge yet, but the create-tie path
      // stages the pair straight from current deps — so the new type LANDS there
      // even though trainEdges is null. Only a level that maps both ends to
      // EXISTING bars and genuinely lacks the old edge truly drops the retype.
      const eitherCreated = (carId: string) =>
        level.rows.some((r) => r.carId === carId && r.kind === "create");
      if (eitherCreated(predCarId) || eitherCreated(succCarId)) continue;
      if (isRetype && key in level.trainEdges && level.trainEdges[key] === null) {
        return true; // no board edge to retype here → the retype never landed
      }
    }
    return false;
  };

  const baselineByPair = new Map(train.baselineDeps.map((d) => [depKey(d), d]));
  const currentByPair = new Map(train.deps.map((d) => [depKey(d), d]));

  // Start from the current deps, then UN-fold any edit that wasn't fully applied:
  //  - an ADD (in deps, not baseline) dropped somewhere → keep OUT of baseline.
  //  - a RETYPE (in both, type/lag changed) dropped somewhere → keep the OLD
  //    baseline entry so the retype stays a live delta on the levels it missed.
  //  - a REMOVE (in baseline, not deps) dropped somewhere → keep IN baseline.
  const nextBaseline: TrainState["deps"] = [];
  currentByPair.forEach((d, key) => {
    const base = baselineByPair.get(key);
    if (!base) {
      // ADD: in deps, not in baseline.
      if (depDropped(d.predCarId, d.succCarId)) return; // not fully applied → not baseline
      nextBaseline.push({ ...d });
      return;
    }
    const isRetype = base.type !== d.type || base.lagDays !== d.lagDays;
    if (isRetype && depDropped(d.predCarId, d.succCarId, true)) {
      // Retype dropped on a level lacking the old board edge → keep it LIVE: carry
      // the OLD baseline entry forward so a later re-Apply still restates that level.
      nextBaseline.push({ ...base });
      return;
    }
    nextBaseline.push({ ...d });
  });
  baselineByPair.forEach((d, key) => {
    if (currentByPair.has(key)) return; // still present in deps → handled above / unchanged
    // A removal (in baseline, not deps) dropped on some level → keep it in baseline.
    if (depDropped(d.predCarId, d.succCarId)) nextBaseline.push({ ...d });
  });

  const cars = train.cars.map((c) =>
    carDropped.has(c.carId)
      ? c
      : { ...c, baselineDurationDays: c.durationDays },
  );

  return { cars, baselineDeps: nextBaseline };
}

/** Count of actionable changes in a review, honoring exclusions — for the
 *  modal summary and the "nothing to apply" guard. */
export function reviewChangeCounts(
  review: TrainApplyReview,
  excluded: ReadonlySet<string> = new Set(),
): { creates: number; resizes: number; ties: number; locked: number } {
  let creates = 0;
  let resizes = 0;
  let ties = 0;
  let locked = 0;
  for (const level of review.levels) {
    const isEx = (carId: string) =>
      excluded.has(exclusionKey(level.instanceIndex, carId));
    for (const row of level.rows) {
      if (row.kind === "locked") locked += 1;
      if (isEx(row.carId)) continue;
      if (row.kind === "create") creates += 1;
      else if (row.kind === "resize") resizes += 1;
    }
    for (const tie of level.ties) {
      if (tie.predCarId && isEx(tie.predCarId)) continue;
      if (tie.succCarId && isEx(tie.succCarId)) continue;
      ties += 1;
    }
  }
  return { creates, resizes, ties, locked };
}
